import { Pencil, Trash2, Eye, EyeOff, Package } from 'lucide-react';
import type { PaqueteVista } from '@shared/types';

interface Props {
  paquete: PaqueteVista;
  categoriaNombre: string;
  moneda: string;
  fmt: (n: number) => string;
  onEditar: () => void;
  onEliminar: () => void;
  onToggleHabilitado: () => void;
}

/**
 * Sprint 17 — Card de paquete con desglose visible.
 *
 * Reemplaza la fila densa por una unidad visual que muestra:
 *  - Nombre + precio prominente.
 *  - Lista de renglones (ordenados por posición) con "+N sust" si tiene sustitutos.
 *  - Precio máximo si hay sustitutos más caros.
 *  - Toggle activo/inactivo inline.
 */
export function PaqueteCard({
  paquete,
  categoriaNombre,
  moneda,
  fmt,
  onEditar,
  onEliminar,
  onToggleHabilitado,
}: Props) {
  const resumen = paquete.resumen;
  const tienePrecioMaximoDistinto =
    resumen && Math.abs(resumen.precioMaximo - paquete.precioBase) > 0.005;

  return (
    <div
      className={`rounded-xl border-2 ${paquete.habilitado ? 'border-slate-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-75'} p-4 flex flex-col gap-3 shadow-sm hover:shadow-md transition-shadow`}
    >
      {/* Header: nombre + categoría + estado */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold leading-tight text-slate-800">{paquete.nombre}</h3>
          <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
            <Package className="h-3 w-3" />
            {categoriaNombre}
          </div>
        </div>
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold tracking-wide ${
            paquete.habilitado
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-slate-200 text-slate-600'
          }`}
        >
          {paquete.habilitado ? 'EN VENTA' : 'OCULTO'}
        </span>
      </div>

      {paquete.descripcion && (
        <p className="text-xs text-slate-600 italic line-clamp-2">{paquete.descripcion}</p>
      )}

      {/* Precio */}
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
        <div className="flex items-baseline justify-between">
          <span className="text-xs uppercase tracking-wide text-slate-500">Precio base</span>
          <span className="font-mono text-xl font-bold text-primary">
            {moneda}{fmt(paquete.precioBase)}
          </span>
        </div>
        {tienePrecioMaximoDistinto && (
          <div className="flex items-baseline justify-between mt-1 pt-1 border-t border-slate-200">
            <span className="text-[11px] text-amber-700">Máx con sustituciones</span>
            <span className="font-mono text-sm font-semibold text-amber-700">
              {moneda}{fmt(resumen!.precioMaximo)}
            </span>
          </div>
        )}
      </div>

      {/* Desglose de renglones */}
      {resumen && resumen.renglones.length > 0 ? (
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-600 mb-1">
            Incluye ({resumen.cantidadRenglones} renglones)
          </div>
          <ul className="text-xs text-slate-700 space-y-0.5 max-h-48 overflow-y-auto pr-1">
            {resumen.renglones.map((r) => (
              <li key={r.posicion} className="flex items-baseline gap-1">
                <span className="text-slate-400 shrink-0">•</span>
                <span className="flex-1 min-w-0 truncate">
                  <span className="font-medium">×{r.cantidad}</span> {r.productoNombre}
                </span>
                {r.cantidadSustitutos > 0 && (
                  <span className="text-[10px] text-amber-600 font-semibold shrink-0">
                    +{r.cantidadSustitutos} sust
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="text-xs text-slate-400 italic">Sin renglones configurados.</div>
      )}

      {/*
        Sprint 60 — El hueco que dejó un producto retirado.
        Al quitar la Coca de 3 L, dos paquetes se quedaron sin bebida y nada lo
        indicaba: el negocio no puede decidir qué poner en su lugar si no ve que
        falta algo. Se muestra tachado, con la invitación a decidir.
      */}
      {resumen?.retirados && resumen.retirados.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5">
          <div className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-amber-800">
            Ya no incluye
          </div>
          <ul className="space-y-0.5 text-xs text-amber-900">
            {resumen.retirados.map((r) => (
              <li key={`ret-${r.posicion}`} className="flex items-baseline gap-1">
                <span className="shrink-0 text-amber-500">•</span>
                <span className="flex-1 min-w-0 truncate line-through decoration-amber-400">
                  ×{r.cantidad} {r.productoNombre}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-1 text-[11px] leading-tight text-amber-800">
            Edita el paquete para poner algo en su lugar, o déjalo así si ya no lo lleva.
          </p>
        </div>
      )}

      {/* Acciones */}
      <div className="flex gap-1.5 border-t border-slate-200 pt-2 -mx-1 mt-auto">
        <button
          onClick={onToggleHabilitado}
          className={`btn-ghost flex-1 ${paquete.habilitado ? 'text-amber-700' : 'text-emerald-700'}`}
          title={paquete.habilitado ? 'Ocultar del menú POS' : 'Mostrar en menú POS'}
        >
          {paquete.habilitado ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          <span className="text-xs">{paquete.habilitado ? 'Ocultar' : 'Mostrar'}</span>
        </button>
        <button onClick={onEditar} className="btn-ghost text-slate-700" aria-label="Editar">
          <Pencil className="h-4 w-4" />
        </button>
        <button onClick={onEliminar} className="btn-ghost text-red-600" aria-label="Eliminar">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
